import { Eye, EyeSlash } from '@phosphor-icons/react'
import { type MouseEventHandler, forwardRef, useCallback, useRef } from 'react'
import { useDisclosure } from '../../hooks/useDisclosure'
import { mergeRefs } from '../../utils'
import { Box } from '../Box'
import { Input, type InputProps, inputDefaultSize } from './Input'

type PasswordInputProps = Omit<InputProps, 'type'> & {
  containerClassName?: string
}

const buttonSizes = {
  small: 35,
  medium: 44,
}

const iconSizes = {
  small: '1em',
  medium: '1.25em',
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function Password({ containerClassName, style, size, ...inputProps }, ref) {
    const inputRef = useRef<HTMLInputElement>(null)
    const { isOpen: isVisible, toggle } = useDisclosure(false)
    const buttonSize = buttonSizes[size ?? inputDefaultSize]
    const iconSize = iconSizes[size ?? inputDefaultSize]

    const toggleVisibility: MouseEventHandler<HTMLElement> = useCallback(() => {
      const input = inputRef.current
      if (input) {
        const isHidden = input.getAttribute('type') === 'password'
        // Modify DOM rather than go through state to avoid unnecessary re-rendering of Input
        input.setAttribute('type', isHidden ? 'text' : 'password')
        toggle()
      }
    }, [toggle])

    return (
      <Box display="flex" alignItems="center" position="relative" style={style}>
        <Input
          {...inputProps}
          size={size}
          style={{ paddingRight: buttonSize }}
          ref={mergeRefs<HTMLInputElement>(ref, inputRef)}
        />
        <Box
          as="button"
          type="button"
          onClick={inputProps.disabled ? undefined : toggleVisibility}
          title="Show password"
          style={{ width: buttonSize, height: buttonSize }}
          display="flex"
          alignItems="center"
          justifyContent="center"
          position="absolute"
          right="0"
        >
          {isVisible ? (
            <EyeSlash
              aria-label="Toggle password masking"
              role="img"
              width={iconSize}
            />
          ) : (
            <Eye
              aria-label="Toggle password masking"
              role="img"
              width={iconSize}
            />
          )}
        </Box>
      </Box>
    )
  },
)
